import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import PageButton from '../../elements/PageButton/PageButton';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {TUTORIAL_CHAPTERS} from '../../../configs/tutorial';
import {getTutorialChapterReward} from '../../../utils/tutorial/getTutorialChapterReward';
import {getFactionCampaigns} from '../../../utils/factions/getFactionCampaigns';
import {getFactionUnitSkinIconPath} from '../../../utils/factions/getFactionUnitSkinIconPath';
import {MissionRewardBadge} from './MissionRewardBadge';
import {checkIfCampaignPlayable} from '../../../configs/campaigns';
import './TutorialMissionsPage.css';

const PAGE_ID = 'TutorialMissionsPage';

// Плашка сюжетной кампании фракции: иконка — фракционный скин отряда
// (getFactionCampaigns), вторая строка — статус готовности кампании.
// Кликов пока нет ни у одной
const FactionCampaignButton = ({campaign, isAdmin, onOpenCampaign}) => {
  // Кампания открывается, только если у фракции есть миссии, доступные этому
  // игроку: невыпущенные (isAdminOnly) видят лишь админы. Для остальных —
  // прежняя заглушка со статусом «В разработке»
  const isPlayable = checkIfCampaignPlayable(campaign.factionId, {isAdmin});

  return (
  <PageButton
    pageId={PAGE_ID}
    buttonId={`campaign_${campaign.factionId}`}
    className={'tutorial-missions-page_mission tutorial-missions-page_campaign'}
    disabled={!isPlayable}
    onClick={isPlayable ? () => onOpenCampaign(campaign.factionId) : undefined}
  >
    <span className={'tutorial-missions-page_mission-main'}>
      <span className={'tutorial-missions-page_mission-status tutorial-missions-page_campaign-icon'}>
        <img
          src={getFactionUnitSkinIconPath(campaign.unitTypeId, campaign.factionId)}
          alt={campaign.title}
        />
      </span>
      <span className={'tutorial-missions-page_mission-info'}>
        <span className={'tutorial-missions-page_mission-title'}>{campaign.title}</span>
        <span className={'tutorial-missions-page_mission-description'}>
          {isPlayable ? 'Кампания доступна' : campaign.statusTitle}
        </span>
      </span>
    </span>
  </PageButton>
  );
};

/**
 * Список миссий кампании. Первые три — главы обучения: последовательная
 * разблокировка (глава N открыта после главы N-1), пройденные можно
 * перепроходить. Дальше сюда встанут сюжетные миссии конструктора.
 */
const TutorialMissionsPage = ({onStartChapter, onCreateMission, onOpenCampaign, onBack}) => {
  usePageAnalytics(PAGE_ID);
  const isAdmin = useSelector((state) => state.private.auth.isAdmin);
  const isSuperAdmin = useSelector((state) => state.private.auth.isSuperAdmin);
  const completedChapters = useSelector(
    (state) => state.private.auth.completedTutorialChapters
  ) || [];
  // Награда главы зависит от игрока: главу с наградой-фракцией тем, у кого она
  // уже открыта, засчитывают запасной наградой (см. getTutorialChapterReward).
  // isAvailable в availableFactions уже учитывает и «все фракции», и стартовых
  // Людей — тем же признаком пользуется FactionSelectPage
  const availableFactions = useSelector((state) => state.private.auth.availableFactions);
  const ownedFactionIds = useMemo(
    () => (availableFactions || []).filter((faction) => faction.isAvailable).map((faction) => faction.id),
    [availableFactions]
  );
  const factionCampaigns = useMemo(() => getFactionCampaigns(ownedFactionIds), [ownedFactionIds]);

  return (
    <>
      <PageButton
        className={'tutorial-missions-floating-back-button'}
        onClick={onBack}
        pageId={PAGE_ID}
        buttonId={'back_floating'}
      >
        <img
          className={'tutorial-missions-floating-back-button_icon'}
          src={'/img/icons/back_navigation.png'}
          alt={'Назад'}
          title={'Назад'}
        />
      </PageButton>

      <h1 className={'tutorial-missions-page_title'}>Кампании</h1>

      <div className={'tutorial-missions-page_list'}>
        {TUTORIAL_CHAPTERS.map((chapter, index) => {
          const isCompleted = completedChapters.includes(chapter.id);
          const previousChapter = TUTORIAL_CHAPTERS[index - 1];
          const isLocked = Boolean(previousChapter) && !completedChapters.includes(previousChapter.id);

          return (
            <PageButton
              key={chapter.id}
              pageId={PAGE_ID}
              buttonId={`mission_${chapter.id}`}
              className={'tutorial-missions-page_mission'}
              disabled={isLocked}
              onClick={() => onStartChapter(chapter.id)}
              title={isLocked ? `Сначала пройди: ${previousChapter.title}` : chapter.description}
            >
              <span className={'tutorial-missions-page_mission-main'}>
                <span className={'tutorial-missions-page_mission-status'}>
                  {isCompleted && (
                    <img src={'/img/icons/check.png'} alt={'Пройдено'}/>
                  )}
                  {!isLocked && !isCompleted && (
                    <img src={'/img/icons/rules_white.png'} alt={'Не пройдено'}/>
                  )}
                  {isLocked && (
                    <img src={'/img/icons/lock_grey.png'} alt={'Недоступно'}/>
                  )}
                </span>
                <span className={'tutorial-missions-page_mission-info'}>
                  <span className={'tutorial-missions-page_mission-title'}>{chapter.title}</span>
                  <span className={'tutorial-missions-page_mission-description'}>{chapter.description}</span>
                </span>
              </span>
              {/* Награда показывается, пока глава не пройдена (не получена) */}
              {!isCompleted && (
                <MissionRewardBadge reward={getTutorialChapterReward(chapter, {ownedFactionIds})}/>
              )}
            </PageButton>
          );
        })}
      </div>

      <div className={'tutorial-missions-page_list'}>
        {factionCampaigns.map((campaign) => (
          <FactionCampaignButton
            key={campaign.factionId}
            campaign={campaign}
            isAdmin={isAdmin || isSuperAdmin}
            onOpenCampaign={onOpenCampaign}
          />
        ))}
      </div>

      <div className={'main-menu-buttons'}>
        {/* Редактор миссий — инструмент разработки кампаний, поэтому только
            для админов (как кнопка «Админка» в главном меню) */}
        {(isAdmin || isSuperAdmin) && onCreateMission && (
          <PageButton
            onClick={onCreateMission}
            pageId={PAGE_ID}
            buttonId={'create_mission'}
            className={'tutorial-missions-page_create-mission'}
          >
            Создать миссию
          </PageButton>
        )}
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId={'back'}>
          Назад
        </PageButton>
      </div>
    </>
  );
};

export default TutorialMissionsPage;
